import React, {useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {toast} from "react-toastify";
import Swal from "sweetalert2";
import TemplateDefault from "../../../components/template-default";
import Card from "../../../components/card";
import Button from "../../../components/button";
import InputForm from "../../../components/input-form";
import {AlertMessage} from "../../../utils/alert-message";

const defaultFields = [
    {name: "name", label: "Nome *", col: "col-span-12"},
];

const NamedResourceForm = ({singularTitle, article, basePath, queryKey, service, fields = defaultFields}) => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState(() => Object.fromEntries(fields.map(({name}) => [name, ""])));
    const [isSaving, setIsSaving] = useState(false);

    const {isFetching} = useQuery({
        queryFn: () => service.getById(id).then((data) => {
            setState(data);
            return data;
        }),
        queryKey: [queryKey, {id}],
        enabled: !!id,
    });

    const showError = (error, fallback) => {
        const message = error.response?.data?.message;
        toast.error(AlertMessage[message] ?? fallback);
    };

    const submit = () => {
        setIsSaving(true);
        const formData = new FormData();
        fields.forEach(({name}) => {
            const value = typeof state[name] === "string" ? state[name].trim() : state[name];
            formData.append(name, value ?? "");
        });
        if (id) formData.append("_method", "PUT");

        const request = id ? service.edit(formData, id) : service.create(formData);
        request
            .then(({data}) => {
                toast.success("Salvo com sucesso.");
                navigate(`${basePath}/form/${data.id}`);
            })
            .catch((error) => showError(error, "Não foi possível salvar"))
            .finally(() => setIsSaving(false));
    };

    const remove = () => {
        Swal.fire({
            icon: "warning",
            title: "Excluir!",
            showCancelButton: true,
            text: `Tem certeza que deseja excluir ${article} ${singularTitle.toLowerCase()}?`,
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: "#e64942",
            reverseButtons: true,
        }).then(({isConfirmed}) => {
            if (!isConfirmed) return;
            setIsSaving(true);
            service.delete(id)
                .then(() => navigate(basePath))
                .catch((error) => showError(error, "Não foi possível excluir"))
                .finally(() => setIsSaving(false));
        });
    };

    return (
        <TemplateDefault title={`Cadastro de ${singularTitle.toLowerCase()}${id ? ` #${id}` : ""}`}
                         isLoading={isFetching || isSaving}>
            <Card title="Dados">
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    {fields.map(({name, label, col = "col-span-12", type = "text"}) => (
                        <InputForm key={name} id={name} name={name} value={state[name] ?? ""}
                                   type={type}
                                   onChange={({target: {value}}) => setState(prev => ({...prev, [name]: value}))}
                                   label={label} col={col}/>
                    ))}
                </div>
            </Card>

            <Card wrapperClass="mt-5">
                <div className="flex justify-end gap-3">
                    {id && <Button onClick={remove} target="danger">Excluir</Button>}
                    <Button onClick={() => navigate(basePath)} target="warning">Cancelar</Button>
                    <Button onClick={submit} target="success">Salvar</Button>
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default NamedResourceForm;
